import React from 'react'
import tw, { styled } from 'twin.macro'
import { motion } from 'framer-motion'
import cardsData from './../data/cards.json'
import { useStateContext } from '../context/stateContext'
import deckRevers from '../assets/card-reverse.jpg'
import useWindowSize from '../hooks/useWindowSize'

const CardSlot = styled(motion.div)`
	${tw`w-14 h-14 xs:w-16 xs:h-16 mobile:w-20 mobile:h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 2xl:w-44 2xl:h-44 `}
	@media (min-width: 1536px) and (max-height: 750px) {
		${tw`2xl:w-40 2xl:h-40`}
	}
	@media (min-width: 1536px) and (max-height: 700px) {
		${tw`2xl:w-36 2xl:h-36`}
	}
	@media (min-width: 1536px) and (max-height: 650px) {
		${tw`2xl:w-32 2xl:h-32`}
	}
`
const CardItem = styled(motion.div)`
	${tw`absolute cursor-pointer w-14 h-14 xs:w-16 xs:h-16 mobile:w-20 mobile:h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 2xl:w-44 2xl:h-44`}
	perspective: 800px;
	transform-style: preserve-3d;
	@media (min-width: 1536px) and (max-height: 750px) {
		${tw`2xl:w-40 2xl:h-40`}
	}
	@media (min-width: 1536px) and (max-height: 700px) {
		${tw`2xl:w-36 2xl:h-36`}
	}
	@media (min-width: 1536px) and (max-height: 650px) {
		${tw`2xl:w-32 2xl:h-32`}
	}
`
const CardFront = styled(motion.div)`
	${tw`absolute flex items-center justify-center bg-contain w-14 h-14 xs:w-16 xs:h-16 mobile:w-20 mobile:h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 2xl:w-44 2xl:h-44 transform-gpu`}
	transform-style: preserve-3d;
	perspective: 800px;
	box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.45);
	backface-visibility: hidden;
	transform-origin: 50% 50%;
	@media (min-width: 1536px) and (max-height: 750px) {
		${tw`2xl:w-40 2xl:h-40`}
	}
	@media (min-width: 1536px) and (max-height: 700px) {
		${tw`2xl:w-36 2xl:h-36`}
	}
	@media (min-width: 1536px) and (max-height: 650px) {
		${tw`2xl:w-32 2xl:h-32`}
	}

	&::after {
		content: '';
		top: 2px;
		left: 2px;
		right: 2px;
		bottom: 2px;
		${tw`absolute`}
		box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
	}
`
const CardBehind = styled(motion.div)`
	${tw`absolute flex items-center justify-center bg-contain w-14 h-14 xs:w-16 xs:h-16 mobile:w-20 mobile:h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 2xl:w-44 2xl:h-44 transform-gpu`}
	transform-style: preserve-3d;
	perspective: 800px;
	box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.45);
	backface-visibility: hidden;
	transform-origin: 50% 50%;
	@media (min-width: 1536px) and (max-height: 750px) {
		${tw`2xl:w-40 2xl:h-40`}
	}
	@media (min-width: 1536px) and (max-height: 700px) {
		${tw`2xl:w-36 2xl:h-36`}
	}
	@media (min-width: 1536px) and (max-height: 650px) {
		${tw`2xl:w-32 2xl:h-32`}
	}

	&::after {
		content: '';
		top: 2px;
		left: 2px;
		right: 2px;
		bottom: 2px;
		${tw`absolute`}
		box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
	}
`

const Card = ({ order, index, cardClickHandle, foldedPairs, toFold, isPair, notPair, firstCardSelected, secondCardSelected, uniqueKey, flipDeckSrc, colorMode, isEven }) => {
	const { isMobile, width } = useWindowSize()
	const { animationState } = useStateContext()
	const fillColor = isEven ? (colorMode === 'yellow' ? 'blue' : 'yellow') : '#fff'
	const [cardData, setCardData] = React.useState({
		index: index,
		uniqueKey: uniqueKey,
		backgroundImage: '',
		frontUrlDE: cardsData[index - 1].frontUrlDE,
	})

	React.useEffect(() => {
		setCardData({
			index: index,
			uniqueKey: uniqueKey,
			backgroundImage: '',
			frontUrlDE: cardsData[index - 1].frontUrlDE,
		})
	}, [index, uniqueKey, flipDeckSrc])

	const onCardClick = () => {
		if (foldedPairs.includes(index)) return
		if (!animationState && !isPair && uniqueKey !== firstCardSelected.key && secondCardSelected.key === '') {
			setCardData((prevState) => ({ ...prevState, backgroundImage: flipDeckSrc }))
			cardClickHandle(index, uniqueKey)
		}
		if (notPair && !animationState) cardClickHandle()
	}

	const transition = {
		delay: 0.5,
		duration: 0.5,
		ease: 'easeInOut',
		type: 'tween',
	}
	const firstCardVariants = {
		initial: {
			y: 0,
			x: 0,
			scale: 1,
			transformOrigin: '50% 50%',
			zIndex: 49,
			transition: {
				duration: 0.4,
				ease: 'easeIn',
			},
			transitionEnd: {
				zIndex: 49,
			},
		},
		animate: {
			zIndex: 999,
			scale: isMobile ? 1.4 : 1.2,
			y: '-50%',
			x: isMobile ? '-130%' : '-117.5%',
			left: '50%',
			top: '50%',
			transition: transition,
		},
		folded: {
			zIndex: 999,
			scale: 0,
			y: 0,
			x: '-100%',
			top: '90%',
			left: '50%',
			transition: {
				duration: 0.3,
				ease: 'easeIn',
			},
		},
		initialAfter: {
			y: 0,
			x: 0,
			zIndex: 999,
			left: '0%',
			top: '0%',
			scale: 1,
			transition: {
				duration: 0.3,
				ease: 'easeInOut',
				type: 'tween',
			},
			transitionEnd: {
				zIndex: 49,
			},
		},
	}
	const secondCardVariants = {
		initial: {
			y: 0,
			x: 0,
			scale: 1,
			transformOrigin: '50% 50%',
			zIndex: 49,
			transition: {
				duration: 0.4,
				ease: 'easeIn',
			},
			transitionEnd: {
				zIndex: 49,
			},
		},
		animate: {
			zIndex: 999,
			scale: isMobile ? 1.4 : 1.2,
			y: '-50%',
			x: isMobile ? '30%' : '17.5%',
			left: '50%',
			top: '50%',
			transition: transition,
		},
		folded: {
			zIndex: 999,
			scale: 0,
			y: 0,
			x: '-100%',
			top: '90%',
			left: '50%',
			transition: {
				duration: 0.3,
				ease: 'easeIn',
			},
		},
		initialAfter: {
			y: 0,
			x: 0,
			transformOrigin: '50% 50%',
			zIndex: 999,
			left: '0%',
			top: '0%',
			scale: 1,
			transition: {
				duration: 0.3,
				ease: 'easeInOut',
				type: 'tween',
			},
			transitionEnd: {
				zIndex: 49,
			},
		},
	}
	const flipAnimation = {
		initial: {
			rotateY: 0,
			scale: 1,
			transition: {
				ease: [0.4, 0.2, 0.2, 1],
				duration: 0.7,
			},
		},
		flipped: {
			rotateY: 180,
			scale: [1, 1.4],
			transition: {
				ease: [0.4, 0.2, 0.2, 1],
				duration: 0.7,
			},
		},
		flippedReverse: {
			rotateY: -180,
			scale: [1, 1.4],
			transition: {
				ease: [0.4, 0.2, 0.2, 1],
				duration: 0.7,
			},
		},
	}

	return (
		<CardSlot
			data-index={index}
			data-unique={uniqueKey}
			onClick={onCardClick}
			style={{
				order: order,
			}}
		>
			{/* {!foldedPairs.includes(index) && ( */}
			<CardItem initial='initial' animate={firstCardSelected.pair === index && isPair && !toFold ? 'animate' : toFold && secondCardSelected.pair === index && isPair ? 'animate' : 'initial'} variants={firstCardSelected.key === uniqueKey ? firstCardVariants : secondCardVariants} transition={{ duration: 0.4 }}>
				<CardFront
					initial='initial'
					style={{
						// backgroundImage: `url(${deckRevers})`,
						WebkitBackfaceVisibility: 'hidden',
					}}
					animate={foldedPairs.includes(index) || firstCardSelected.key === uniqueKey || secondCardSelected.key === uniqueKey ? 'flipped' : 'initial'}
					variants={flipAnimation}
					transition={{ duration: 0.4 }}
					// transitionEnd={{ zIndex: 49 }}
				>
					<svg xmlns='http://www.w3.org/2000/svg' className='w-full h-full' fill='none' viewBox='0 0 175 176'>
						<path fill='#F1F1F1' d='M0 0h175v176H0z' />
						<g clipPath='url(#a)'>
							<path fill={fillColor} d='M175 .44H0v174.999h175v-175Z' />
							<path
								fill={colorMode === 'yellow' && isEven ? 'white' : 'black'}
								fillRule='evenodd'
								d='M46.17 92.164a6.383 6.383 0 0 0-1.465-.934 15.611 15.611 0 0 0-1.932-.714 20.366 20.366 0 0 0-1.209-.366 5.755 5.755 0 0 1-.906-.311c-.247-.11-.44-.23-.559-.357a.669.669 0 0 1-.192-.504c0-.275.11-.467.339-.586.22-.11.522-.174.906-.174.467 0 .943.083 1.429.23a9.95 9.95 0 0 1 1.318.493c.467.202.915.44 1.346.696l1.822-3.506c-.34-.174-.742-.357-1.181-.559a16.52 16.52 0 0 0-1.401-.549 13.48 13.48 0 0 0-1.566-.412 8.136 8.136 0 0 0-1.657-.165c-.769 0-1.492.11-2.197.339-.687.22-1.3.559-1.84.989-.531.44-.962.97-1.273 1.63-.311.659-.467 1.41-.467 2.26 0 .56.083 1.072.23 1.511.146.44.384.824.713 1.163.312.339.724.64 1.218.916.494.274 1.08.521 1.767.75.467.165.906.302 1.318.412.412.11.778.23 1.099.339.311.11.577.247.75.394a.72.72 0 0 1 .275.558c0 .202-.091.366-.274.495-.174.118-.504.192-.97.192a6.3 6.3 0 0 1-1.795-.257 11.142 11.142 0 0 1-1.575-.586c-.55-.247-1.08-.53-1.593-.851l-1.822 3.726c.467.275.97.522 1.51.742.55.22 1.108.412 1.704.558.586.165 1.18.284 1.767.366a12.61 12.61 0 0 0 1.739.138 9.24 9.24 0 0 0 2.124-.248 5.682 5.682 0 0 0 1.895-.823 4.132 4.132 0 0 0 1.346-1.511c.339-.632.503-1.41.503-2.344 0-.723-.11-1.346-.33-1.85a3.69 3.69 0 0 0-.933-1.3'
								clipRule='evenodd'
							/>
							<path
								stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'}
								strokeMiterlimit={10}
								strokeWidth={0.03}
								d='M46.17 92.164a6.383 6.383 0 0 0-1.465-.934 15.611 15.611 0 0 0-1.932-.714 20.366 20.366 0 0 0-1.209-.366 5.755 5.755 0 0 1-.906-.311c-.247-.11-.44-.23-.559-.357a.669.669 0 0 1-.192-.504c0-.275.11-.467.339-.586.22-.11.522-.174.906-.174.467 0 .943.083 1.429.23a9.95 9.95 0 0 1 1.318.493c.467.202.915.44 1.346.696l1.822-3.506c-.34-.174-.742-.357-1.181-.559a16.52 16.52 0 0 0-1.401-.549 13.48 13.48 0 0 0-1.566-.412 8.136 8.136 0 0 0-1.657-.165c-.769 0-1.492.11-2.197.339-.687.22-1.3.559-1.84.989-.531.44-.962.97-1.273 1.63-.311.659-.467 1.41-.467 2.26 0 .56.083 1.072.23 1.511.146.44.384.824.713 1.163.312.339.724.64 1.218.916.494.274 1.08.521 1.767.75.467.165.906.302 1.318.412.412.11.778.23 1.099.339.311.11.577.247.75.394a.72.72 0 0 1 .275.558c0 .202-.091.366-.274.495-.174.118-.504.192-.97.192a6.3 6.3 0 0 1-1.795-.257 11.142 11.142 0 0 1-1.575-.586c-.55-.247-1.08-.53-1.593-.851l-1.822 3.726c.467.275.97.522 1.51.742.55.22 1.108.412 1.704.558.586.165 1.18.284 1.767.366a12.61 12.61 0 0 0 1.739.138 9.24 9.24 0 0 0 2.124-.248 5.682 5.682 0 0 0 1.895-.823 4.132 4.132 0 0 0 1.346-1.511c.339-.632.503-1.41.503-2.344 0-.723-.11-1.346-.33-1.85a3.69 3.69 0 0 0-.933-1.3'
							/>
							<path
								fill={colorMode === 'yellow' && isEven ? 'white' : 'black'}
								fillRule='evenodd'
								d='M59.747 99.232a6.964 6.964 0 0 0 1.62-1.135 4.46 4.46 0 0 0 1.053-1.52l-3.47-2.069c-.137.339-.311.64-.53.879-.22.229-.477.44-.77.586a3.927 3.927 0 0 1-.879.33 4.302 4.302 0 0 1-.888.091c-.55 0-1.053-.11-1.492-.339a3.287 3.287 0 0 1-1.126-.906 4.224 4.224 0 0 1-.696-1.3 4.874 4.874 0 0 1-.229-1.547c0-.476.064-.962.201-1.456.138-.476.34-.916.632-1.3.284-.385.641-.696 1.08-.934.44-.229.971-.357 1.575-.357.284 0 .586.028.907.083.31.054.613.164.906.302.284.146.558.338.796.585.23.248.44.559.577.934l3.25-2.307c-.53-1.016-1.355-1.84-2.453-2.453-1.099-.614-2.399-.934-3.891-.934-1.245 0-2.362.22-3.333.668a8.034 8.034 0 0 0-2.48 1.767 7.461 7.461 0 0 0-1.566 2.472 7.62 7.62 0 0 0-.531 2.783 8.145 8.145 0 0 0 2.16 5.484 8.102 8.102 0 0 0 2.472 1.822c.943.448 2.005.687 3.159.687.659 0 1.327-.083 2.014-.23a8.973 8.973 0 0 0 1.932-.695'
								clipRule='evenodd'
							/>
							<path
								stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'}
								strokeMiterlimit={10}
								strokeWidth={0.03}
								d='M59.747 99.232a6.964 6.964 0 0 0 1.62-1.135 4.46 4.46 0 0 0 1.053-1.52l-3.47-2.069c-.137.339-.311.64-.53.879-.22.229-.477.44-.77.586a3.927 3.927 0 0 1-.879.33 4.302 4.302 0 0 1-.888.091c-.55 0-1.053-.11-1.492-.339a3.287 3.287 0 0 1-1.126-.906 4.224 4.224 0 0 1-.696-1.3 4.874 4.874 0 0 1-.229-1.547c0-.476.064-.962.201-1.456.138-.476.34-.916.632-1.3.284-.385.641-.696 1.08-.934.44-.229.971-.357 1.575-.357.284 0 .586.028.907.083.31.054.613.164.906.302.284.146.558.338.796.585.23.248.44.559.577.934l3.25-2.307c-.53-1.016-1.355-1.84-2.453-2.453-1.099-.614-2.399-.934-3.891-.934-1.245 0-2.362.22-3.333.668a8.034 8.034 0 0 0-2.48 1.767 7.461 7.461 0 0 0-1.566 2.472 7.62 7.62 0 0 0-.531 2.783 8.145 8.145 0 0 0 2.16 5.484 8.102 8.102 0 0 0 2.472 1.822c.943.448 2.005.687 3.159.687.659 0 1.327-.083 2.014-.23a8.973 8.973 0 0 0 1.932-.695'
							/>
							<path fill={colorMode === 'yellow' && isEven ? 'white' : 'black'} fillRule='evenodd' d='M67.728 93.977h5.182v6.061h4.257V84.565H72.91v5.713h-5.182v-5.713h-4.247v15.473h4.247v-6.061Z' clipRule='evenodd' />
							<path stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'} strokeMiterlimit={10} strokeWidth={0.03} d='M67.728 93.977h5.182v6.061h4.257V84.565H72.91v5.713h-5.182v-5.713h-4.247v15.473h4.247v-6.061Z' />
							<path fill={colorMode === 'yellow' && isEven ? 'white' : 'black'} fillRule='evenodd' d='m82.771 91.89 2.591 5.73h2.27l2.592-5.73v8.148h4.248V84.565h-4.624l-3.36 7.288-3.332-7.288h-4.633v15.473h4.248V91.89Z' clipRule='evenodd' />
							<path stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'} strokeMiterlimit={10} strokeWidth={0.03} d='m82.771 91.89 2.591 5.73h2.27l2.592-5.73v8.148h4.248V84.565h-4.624l-3.36 7.288-3.332-7.288h-4.633v15.473h4.248V91.89Z' />
							<path fill={colorMode === 'yellow' && isEven ? 'white' : 'black'} d='M100.064 84.565h-4.248v15.473h4.248V84.565Z' />
							<path stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'} strokeMiterlimit={10} strokeWidth={0.03} d='M100.064 84.565h-4.248v15.473h4.248V84.565Z' />
							<path fill={colorMode === 'yellow' && isEven ? 'white' : 'black'} fillRule='evenodd' d='M114.329 84.565h-13.165v3.708h4.468v11.765h4.257V88.273H118.797v11.765h4.257V88.273h4.44v-3.708h-13.165Z' clipRule='evenodd' />
							<path stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'} strokeMiterlimit={10} strokeWidth={0.03} d='M114.329 84.565h-13.165v3.708h4.468v11.765h4.257V88.273H118.797v11.765h4.257V88.273h4.44v-3.708h-13.165Z' />
							<path
								fill={colorMode === 'yellow' && isEven ? 'white' : 'black'}
								fillRule='evenodd'
								d='M139.854 93.456a3.693 3.693 0 0 0-.934-1.3 6.383 6.383 0 0 0-1.465-.934 15.58 15.58 0 0 0-1.932-.714 20.248 20.248 0 0 0-1.208-.366 5.732 5.732 0 0 1-.906-.311c-.248-.11-.44-.229-.559-.357a.67.67 0 0 1-.192-.504c0-.274.11-.467.339-.586.219-.11.521-.174.906-.174.467 0 .943.083 1.428.23.476.146.934.31 1.318.494.467.201.916.44 1.355.695l1.822-3.506c-.338-.174-.741-.357-1.181-.558a16.503 16.503 0 0 0-1.4-.55 13.551 13.551 0 0 0-1.566-.412 8.133 8.133 0 0 0-1.657-.164c-.769 0-1.492.11-2.197.338-.687.22-1.3.559-1.84.989-.531.44-.962.97-1.273 1.63-.311.659-.467 1.41-.467 2.26 0 .56.083 1.072.229 1.511.146.44.384.824.714 1.163.311.339.723.641 1.218.916.494.274 1.08.522 1.767.75.467.165.906.303 1.318.412.412.11.778.23 1.099.34.311.109.576.246.75.393a.72.72 0 0 1 .275.558c0 .202-.092.367-.275.495-.174.119-.503.192-.97.192a6.306 6.306 0 0 1-1.795-.256 11.177 11.177 0 0 1-1.574-.586 13.202 13.202 0 0 1-1.593-.852l-1.822 3.726c.467.275.97.522 1.511.742.549.22 1.107.412 1.702.558.586.165 1.181.284 1.767.367.586.082 1.181.137 1.74.137.714 0 1.428-.083 2.124-.247a5.681 5.681 0 0 0 1.895-.824 4.244 4.244 0 0 0 1.355-1.51c.339-.633.503-1.41.503-2.345 0-.723-.109-1.345-.329-1.849'
								clipRule='evenodd'
							/>
							<path
								stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'}
								strokeMiterlimit={10}
								strokeWidth={0.03}
								d='M139.854 93.456a3.693 3.693 0 0 0-.934-1.3 6.383 6.383 0 0 0-1.465-.934 15.58 15.58 0 0 0-1.932-.714 20.248 20.248 0 0 0-1.208-.366 5.732 5.732 0 0 1-.906-.311c-.248-.11-.44-.229-.559-.357a.67.67 0 0 1-.192-.504c0-.274.11-.467.339-.586.219-.11.521-.174.906-.174.467 0 .943.083 1.428.23.476.146.934.31 1.318.494.467.201.916.44 1.355.695l1.822-3.506c-.338-.174-.741-.357-1.181-.558a16.503 16.503 0 0 0-1.4-.55 13.551 13.551 0 0 0-1.566-.412 8.133 8.133 0 0 0-1.657-.164c-.769 0-1.492.11-2.197.338-.687.22-1.3.559-1.84.989-.531.44-.962.97-1.273 1.63-.311.659-.467 1.41-.467 2.26 0 .56.083 1.072.229 1.511.146.44.384.824.714 1.163.311.339.723.641 1.218.916.494.274 1.08.522 1.767.75.467.165.906.303 1.318.412.412.11.778.23 1.099.34.311.109.576.246.75.393a.72.72 0 0 1 .275.558c0 .202-.092.367-.275.495-.174.119-.503.192-.97.192a6.306 6.306 0 0 1-1.795-.256 11.177 11.177 0 0 1-1.574-.586 13.202 13.202 0 0 1-1.593-.852l-1.822 3.726c.467.275.97.522 1.511.742.549.22 1.107.412 1.702.558.586.165 1.181.284 1.767.367.586.082 1.181.137 1.74.137.714 0 1.428-.083 2.124-.247a5.681 5.681 0 0 0 1.895-.824 4.244 4.244 0 0 0 1.355-1.51c.339-.633.503-1.41.503-2.345 0-.723-.109-1.345-.329-1.849'
							/>
							<path fill={colorMode === 'yellow' && isEven ? 'white' : 'black'} fillRule='evenodd' d='m77.259 111.134.686-2.28.66 2.28h-1.346Zm-1.74 0 .687-2.28.66 2.28h-1.347Zm3.525-9.549h-3.882l-5.072 14.593-5.401-7.663 5.621-6.921h-4.788l-4.706 5.887v-5.887h-4.248v15.472h4.248v-4.467l1.181-1.218 3.836 5.694h8.295l.915-3.03h4.111l.934 3.03h4.321l-5.374-15.472.01-.018Z' clipRule='evenodd' />
							<path stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'} strokeMiterlimit={10} strokeWidth={0.03} d='m77.259 111.134.686-2.28.66 2.28h-1.346Zm-1.74 0 .687-2.28.66 2.28h-1.347Zm3.525-9.549h-3.882l-5.072 14.593-5.401-7.663 5.621-6.921h-4.788l-4.706 5.887v-5.887h-4.248v15.472h4.248v-4.467l1.181-1.218 3.836 5.694h8.295l.915-3.03h4.111l.934 3.03h4.321l-5.374-15.472.01-.018Z' />
							<path fill={colorMode === 'yellow' && isEven ? 'white' : 'black'} fillRule='evenodd' d='M106.639 104.743v-3.158H81.838v3.708h4.477v11.764h4.248v-11.764h11.178l-7.223 8.615v3.149h12.258v-3.699h-6.93l6.793-8.615Z' clipRule='evenodd' />
							<path stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'} strokeMiterlimit={10} strokeWidth={0.03} d='M106.639 104.743v-3.158H81.838v3.708h4.477v11.764h4.248v-11.764h11.178l-7.223 8.615v3.149h12.258v-3.699h-6.93l6.793-8.615Z' />
							<path fill={colorMode === 'yellow' && isEven ? 'white' : 'black'} fillRule='evenodd' d='M108.122 105.293h6.582v2.179h-5.63v3.442h5.63v2.444h-6.774v3.699h11.022v-15.472h-10.83v3.708Z' clipRule='evenodd' />
							<path stroke={colorMode === 'yellow' && isEven ? 'white' : 'black'} strokeMiterlimit={10} strokeWidth={0.03} d='M108.122 105.293h6.582v2.179h-5.63v3.442h5.63v2.444h-6.774v3.699h11.022v-15.472h-10.83v3.708Z' />
							<path fill={colorMode === 'yellow' && isEven ? 'white' : 'black'} d='M97.93 71.685h-8.578l6.95-3.003-.523-1.2-8.267 3.58-8.267-3.58-.522 1.2 6.95 3.003h-8.58v1.309h8.58l-6.95 3.003.522 1.199 8.267-3.58 8.267 3.58.522-1.2-6.949-3.002h8.579v-1.31Z' />
						</g>
						<path stroke='#E6FF00' d='M.5.939h174v174H.5z' />
						<defs>
							<clipPath id='a'>
								<path fill='#fff' d='M0 .439h175v175H0z' />
							</clipPath>
						</defs>
					</svg>
				</CardFront>
				<CardBehind
					initial='flippedReverse'
					style={{
						backgroundImage: `url(${cardData.backgroundImage})`,
						WebkitBackfaceVisibility: 'hidden',
					}}
					animate={foldedPairs.includes(index) || firstCardSelected.key === uniqueKey || secondCardSelected.key === uniqueKey ? 'initial' : 'flippedReverse'}
					variants={flipAnimation}
					transition={{ duration: 0.5 }}
				>
					{cardData.frontUrlDE}
				</CardBehind>
			</CardItem>
			{/* )} */}
		</CardSlot>
	)
}

export default Card
